import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Radio, ArrowLeft, AlertTriangle } from "lucide-react";
import { DEFCON_COLORS, DEFCON_ORDER } from "@/lib/defcon";
import DefconHistory from "@/components/DefconHistory";
import StreamPlayer from "@/components/StreamPlayer";

function fmtCountdown(ms) {
  if (ms == null || isNaN(ms) || ms <= 0) return "starting now";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s % 60}s`;
  return `${m}m ${s % 60}s`;
}

// Dedicated public DEFCON status page for Crashout.
export default function DefconPage() {
  const [d, setD] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const load = async () => {
    try { const { data } = await api.get("/defcon"); setD(data); } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!d) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[var(--ff-text-dim)] font-mono-ui text-xs uppercase tracking-[0.25em] animate-pulse">Loading…</div>;
  }

  const level = d.current_level || 5;
  const color = DEFCON_COLORS[level] || "#FF3B30";
  const meta = (d.levels && d.levels[String(level)]) || {};
  const nuclear = level === 1;
  const countdown = d.next_air ? fmtCountdown(new Date(d.next_air).getTime() - now) : "";

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12" data-testid="defcon-page">
      <Link to={d.show_id ? `/shows/${d.show_id}` : "/schedule"} className="inline-flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.15em] text-[var(--ff-text-dim)] hover:text-white mb-8">
        <ArrowLeft size={13} /> Back to {d.show_title}
      </Link>

      <div className="label-eyebrow mb-4 flex items-center gap-2" style={{ color }}>
        <Radio size={13} /> {d.name || "Freshy Defcon"} · {d.show_title}
      </div>

      <div
        className="relative overflow-hidden border p-8 mb-10"
        style={{ borderColor: `${color}66`, background: `linear-gradient(135deg, ${color}18 0%, transparent 60%)` }}
      >
        {nuclear && <div className="pointer-events-none absolute inset-0 defcon-flash" style={{ background: `${color}0d` }} />}
        <div className="relative flex flex-col lg:flex-row lg:items-end gap-8">
          <div className="shrink-0">
            <div className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[var(--ff-text-dim)] mb-1">Current Status</div>
            <div className="flex items-end gap-4">
              <div className="font-display leading-none" style={{ fontSize: "6rem", color }} data-testid="defcon-page-level">{level}</div>
              <div className="pb-3">
                <div className="font-display text-4xl leading-tight">{meta.label}</div>
                {nuclear && (
                  <div className="flex items-center gap-1 mt-1 font-mono-ui text-xs uppercase tracking-[0.15em]" style={{ color }}>
                    <AlertTriangle size={12} /> Go Nuclear
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex-1 w-full space-y-1.5">
            {DEFCON_ORDER.map((lvl) => {
              const active = lvl === level;
              const c = DEFCON_COLORS[lvl];
              const lm = (d.levels && d.levels[String(lvl)]) || {};
              return (
                <div key={lvl} className="flex items-center gap-3 px-3 py-2 border" style={{ borderColor: active ? c : "var(--ff-border)", background: active ? `${c}22` : "transparent", opacity: active ? 1 : 0.55 }}>
                  <span className="font-display text-xl w-7 text-center" style={{ color: active ? c : "var(--ff-text-dim)" }}>{lvl}</span>
                  <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                  <span className={`font-mono-ui text-xs uppercase tracking-[0.12em] ${active ? "text-white" : "text-[var(--ff-text-dim)]"}`}>{lm.label}</span>
                </div>
              );
            })}
          </div>
        </div>
        {meta.description && <p className="relative mt-6 text-[var(--ff-text-dim)] leading-relaxed max-w-3xl">{meta.description}</p>}
      </div>

      {nuclear && (d.next_air_label || d.stream_url) && (
        <div className="border p-6 mb-10" style={{ borderColor: `${color}66` }} data-testid="defcon-page-live">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 mb-5">
            <div className="flex-1">
              <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ff-text-dim)] mb-1">Goes Live</div>
              <div className="font-display text-3xl">{d.next_air_label || "TBA"}</div>
              {countdown && <div className="font-mono-ui text-sm mt-1" style={{ color }}>{countdown}</div>}
            </div>
          </div>
          {d.stream_url && <StreamPlayer url={d.stream_url} color={color} />}
        </div>
      )}

      <DefconHistory className="border-t border-[var(--ff-border)] pt-8" />
    </div>
  );
}
